import { canv } from "@/canv/global";
import { generateUID } from "@/canv/utils";
import type { Group } from "@/group";
import type { EntityBaseState } from "@/shared/types/entityState";
import type { WidgetConstructorName } from "@/shared/types/widgetConstructorName";
import type { LocateWidgetMenuResult, SearchTextObject } from "./types";

export class ElementBase {
    constructor() {
        this.UID = generateUID();
    }

    TryAction(type: string): void {}

    draw(): void {}

    drawOver(): void {}

    shouldDraw(): boolean | undefined {
        return this.parent.isGroupOnScreen();
    }

    clearText(): void {}

    translate(x: number, y: number): void {}

    resize(x: number, y: number, catchPointer: string): void {}

    updateDims(withBaseParent = true): void {}

    updateBase(
        startX: number,
        startY: number,
        width: number,
        height: number,
        withBaseParent = true,
    ) {
        this.baseStartX = startX;
        this.baseStartY = startY;
        this.baseWidth = width;
        this.baseHeight = height;

        if (withBaseParent && this.parent != null) {
            this.parent.bottomTopUpdateDims();
        }
    }

    beforeDeletion(): void {}

    translatingStart(): void {}

    translatingStop(): void {}

    rotatingStart(): void {}

    rotatingStop(): void {}

    resizingStart(): void {}

    resizingStop(catchPointer: string): void {}

    isInteraction(context: string, force = false): any {
        /*
            return {
                "type": none | click | interaction;
                // было что-то еще?
            };
        */
        return { type: "none" };
    }

    shouldStopInteractionOnModeChange(_nextMode: string) {
        return true;
    }

    stopInteraction() {}

    /**
     *
     * @param {boolean} isCandidate есть ли виджет поверх текущего
     * @returns {boolean}
     */
    isOver(isCandidate = false) {
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        // Преобразования нужные, чтобы сравниваться с x, y (и не нужны чтобы рисовать в makeSelected)
        const realStartPixX = this.baseStartX * canv.m[0];
        const realStartPixY = this.baseStartY * canv.m[3];
        const realWidth = this.baseWidth * canv.m[0];
        const realHeight = this.baseHeight * canv.m[3];

        if (
            x >= realStartPixX &&
            x <= realStartPixX + realWidth &&
            y >= realStartPixY &&
            y <= realStartPixY + realHeight
        ) {
            //return true;
        }

        if (
            x >= this.baseStartX &&
            x <= this.baseStartX + this.baseWidth &&
            y >= this.baseStartY &&
            y <= this.baseStartY + this.baseHeight
        ) {
            return true;
        }

        return false;
    }

    isInArea(area) {
        // для simulated блока : начало левее и выше правого нижнего угла this фигуры, а конец правее и ниже начала this

        if (
            area.x <= this.baseStartX + this.baseWidth &&
            area.y <= this.baseStartY + this.baseHeight &&
            area.x + area.width >= this.baseStartX &&
            area.y + area.height >= this.baseStartY
        ) {
            return true;
        }

        return false;
    }

    isCatched() {
        return false;
    }

    showCatchPointer() {}

    // Для menu
    /**
     *
     * @param {Boolean} [isLocked] - group.isLocked
     */
    getWidgetOptions(isLocked: boolean) {}

    /**
     *
     * @param {string} name
     * @returns {Function | void}
     */
    getHandlerForOption(name: string): ((...args: any[]) => any) | undefined | void {}

    locateWidgetMenu(): LocateWidgetMenuResult {
        return { status: false };
    }

    // SET PARENT - единственный способ выставлять parentUID (кроме loadState) и аболютно единственны this.parent
    setParent(group: Group, disableRemoving?: boolean) {
        this.parentUID = group.UID;
        this.parent = group;
        group.children.push(this);
        this.updateBase(this.baseStartX, this.baseStartY, this.baseWidth, this.baseHeight);
    }

    isNeedInit() {
        return false;
    }

    initWidget() {}

    checkDeletion() {
        return true;
    }

    regenerateUIDS() {}

    // Далее для states

    buildProperty(property, fixType, action) {}

    checkEvent(state, fixType) {
        return true;
    }

    getFixType(reason) {}

    getState(fixType, twinState?: unknown) {}

    loadState(state, fixType) {}

    fixState(reason) {
        const fixType = this.getFixType(reason);

        this.statesMap[reason] = {
            fixType: fixType,
            state: this.getState(fixType),
        };
    }

    saveState(reason?: string) {
        if (!reason || this.statesMap[reason] === undefined) {
            return;
        }

        const fixType = this.statesMap[reason].fixType;

        canv.eventManager.createPropertyEvent(
            this.UID,
            fixType,
            this.statesMap[reason].state,
            this.getState(fixType) as unknown as EntityBaseState,
        );
    }

    /**
     * @typedef {object} SearchResult
     * @property {string} mode
     * @property {boolean} status
     * @property {textWidget} textWidget
     */

    /**
     *
     * @param {string} text search text
     * @returns {Array.<SearchResult>}
     */
    searchText(text: string): SearchTextObject[] {
        return [];
    }

    reset() {}

    becomingFocused() {}

    becomingSelected() {}

    fixedState = "";
    fixType = "";
    unsavedState = false;
    statesMap = {};

    constructorName: WidgetConstructorName | "" = "";
    UID = "";
    parentUID = "";
    trailUID = "";
    anchorUID = "";

    baseStartX = 0;
    baseStartY = 0;
    baseWidth = 0;
    baseHeight = 0;

    /**
     * @type {Group | null}
     */
    // biome-ignore lint/style/noNonNullAssertion: Родитель устанавливается через setParent.
    parent: Group = null!;

    rotatable = true;
    isSelected = false;

    customResize = false; // используется для стрелки
    allowSideResize = false; // используется для карточки
    allowOptionsForLocked = false; // для стрелок и фреймов
    fastAction = "classic";

    catchPointer = "";
    pointerSize = 0;

    disableConnectors = false; // используется, чтобы вокруг стрелок небыло connectors
    disableLevelLines = false; // используется, чтобы стрелки не давали levelLines и чтобы стрелки не магнитились к levelLines
    disableOutline = false; // используется, чтобы не рисовать outline в setSelected, addSelected и cursorTool outline для стрелок
    nothingWhenInteraction = false; // используется, чтобы не рисовать outline, catchPointers... в interaction mode (например table)
    nothingWhenInteractionLowOutline = false; // используется, чтобы рисовать low outline при nothingWhenInteraction (у textWidget)

    widgetOptions = [];
}
